import React, { useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  TextField,
  Chip,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { useFamily } from '../../context/FamilyContext';
import { useSkillGraph } from '../../hooks/useSkillGraph';
import { useLearnerMastery } from '../../hooks/useLearnerMastery';
import { useNextSkills } from '../../hooks/useNextSkills';
import { effectiveStatus } from '../../utils/masteryStatus';

const SUBJECT_LABEL: Record<string, string> = {
  math: 'Math',
  reading: 'Reading',
  writing: 'Writing',
};

/**
 * Parent-initiated entry point for assessments - reachable any time from the
 * nav, not just as a one-time wall right after adding a child (that gate
 * lived in MasteryMap and blocked seeing the dashboard at all; see its
 * "quick check" prompt for the softer version of the same nudge). Offers two
 * ways in: a subject-wide placement sweep (existing whole-graph placement,
 * scoped by db_backend/services/placementService.js's subject filter), or
 * picking one specific skill to check (reuses SkillDetail's existing Recheck
 * tab - nothing new needed there).
 */
const AssessmentsHub: React.FC = () => {
  const navigate = useNavigate();
  const { learners, loading: familyLoading } = useFamily();
  const learner = learners[0] || null;

  const { nodes, loading: graphLoading } = useSkillGraph();
  const { mastery, loading: masteryLoading } = useLearnerMastery(learner?._id || null);
  const { needsCheck, loading: needsCheckLoading } = useNextSkills(learner?._id || null, { limit: 3 });

  const [search, setSearch] = useState('');

  const loading = familyLoading || graphLoading || masteryLoading;

  const subjects = useMemo(
    () => Array.from(new Set(nodes.map((n) => n.subject))).filter(Boolean),
    [nodes]
  );

  const statusBySkillId = useMemo(() => {
    const map = new Map(mastery.map((m) => [m.skillId._id, m]));
    return (skillId: string) => effectiveStatus(map.get(skillId));
  }, [mastery]);

  const filteredNodes = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sorted = [...nodes].sort((a, b) => a.gradeLevel - b.gradeLevel || a.name.localeCompare(b.name));
    if (!q) return sorted;
    return sorted.filter((n) => n.name.toLowerCase().includes(q) || n.code.toLowerCase().includes(q));
  }, [nodes, search]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!learner) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Add a child to get started
          </Typography>
          <Button variant="contained" onClick={() => navigate('/onboarding')}>
            Add a Child
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
        Assessments
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
        Check where {learner.firstName} stands, any time - not just when a child is first added.
      </Typography>

      {!needsCheckLoading && needsCheck.length > 0 && (
        <Paper sx={{ p: 3, mb: 4, backgroundColor: 'warning.50' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Quick checks that would help right now
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            These skills aren't recommended yet only because a prerequisite has never
            been checked - not because {learner.firstName} is struggling with it.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {needsCheck.map((item) => (
              <Box
                key={item.skillId}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 1.5,
                  backgroundColor: 'background.paper',
                  borderRadius: 1,
                }}
              >
                <Box>
                  <Typography variant="subtitle2">{item.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Blocked by: {item.blockedBy.map((b) => b.name).join(', ')}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    navigate(`/dashboard/skills/${item.blockedBy[0].skillId}?learnerId=${learner._id}`)
                  }
                >
                  Check now
                </Button>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Check a whole subject
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          A broad check (about 15-20 minutes) across every skill in one subject.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {subjects.map((subject) => (
            <Button
              key={subject}
              variant="contained"
              onClick={() => navigate(`/dashboard/placement/${learner._id}?subject=${subject}`)}
            >
              Check {SUBJECT_LABEL[subject] || subject}
            </Button>
          ))}
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Check one specific skill
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          A couple of quick questions on just this skill.
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Search skills..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ maxHeight: 400, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
          {filteredNodes.map((node) => (
            <Box
              key={node.id}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label={`Grade ${node.gradeLevel}`} size="small" variant="outlined" />
                <Typography variant="body2">{node.name}</Typography>
              </Box>
              <Button
                size="small"
                onClick={() => navigate(`/dashboard/skills/${node.id}?learnerId=${learner._id}`)}
              >
                {statusBySkillId(node.id) === 'not-attempted' ? 'Check this skill' : 'Recheck this skill'}
              </Button>
            </Box>
          ))}
          {filteredNodes.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No skills match "{search}".
            </Typography>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default AssessmentsHub;
